import React, { useCallback, useEffect, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";

import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import GridViewIcon from "@mui/icons-material/GridView";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { IconProps, Stack } from "@mui/material";
import { HorizontalNavBar, RoleComponentGuard } from "components";
import { InlineFilter } from "components/Filters/InlineFilter";
import { SelectDateRange } from "components/forms";
import { SetPicker } from "components/reports-cashflow";
import { Routes } from "constants/routes";
import { useActionHook, useModals } from "hooks";
import {
  useAccountSelector,
  useDealActions,
  useReportsActions,
  useReportsFiltersSelector,
  useReportsQueriesSelector
} from "store";
import { DateRange, DateRangeValueType, ReportFilters } from "store/types";

import { ContextMenuReports, GroupByContent, GroupByMenu, GroupByMenuSettings, SelectColumnContent } from "..";
import { RoleName } from "../../../constants";
import { NumberFormat } from "../Number-format/NumberFormat";
import { SelectCurrency } from "../Select-currency/SelectCurrency";

const iconsSx: IconProps["sx"] = {
  fontSize: "16px",
  mr: "6px"
};

const isInTheFuture = (date: string): boolean => {
  const today = new Date();
  today.setHours(23, 59, 59, 998);
  return new Date(date) > today;
};

interface QueryManagerProps extends GroupByMenuSettings {
  riskMetrics?: boolean;
  showGroupByMenu?: boolean;
  showRowBy?: boolean;
  showCurrency?: boolean;
  showFilters?: boolean;
  /** When provided, merges these values into the store's filter state on mount (e.g. Investor names for scoped reporting). */
  filtersOverride?: Partial<Record<string, (number | string | boolean)[]>>;
  /** When true, removes the Investor key from the InlineFilter dropdown so the user cannot add it again. */
  hideInvestorFilter?: boolean;
  /** When true, prevents the Investor filter from being modified via InlineFilter while allowing other filters to change. */
  lockInvestorSelection?: boolean;
  /** When true, renders the HorizontalNavBar. Set to false when the parent provides its own top navigation. */
  showNavBar?: boolean;
}

export const QueryManager: React.FC<QueryManagerProps> = ({
  showGroupByMenu = false,
  showCharacteristics = false,
  showColumns = false,
  showGroupBy = true,
  showRowBy = true,
  showCurrency = true,
  showFilters = true,
  filtersOverride,
  hideInvestorFilter = false,
  lockInvestorSelection = false,
  showNavBar = true
}) => {
  // 1. ALL HOOKS THAT RETURN ACTIONS MUST GO FIRST TO AVOID TDZ
  const { setQueriesAction, getFilters } = useReportsActions();
  const { updateQueries } = useDealActions();
  const { errorModal, noticeModal } = useModals();
  const { location } = useHistory();

  // 2. SELECTORS AND OTHER HOOKS
  const { dateRange, comparisonColumns, secondaryDateRange } = useReportsQueriesSelector();
  const { show_cashflow, disable_apply_filters, show_global_investor_filter } = useAccountSelector() || {};
  const stateFilters = useReportsFiltersSelector();
  const { pathname } = useLocation();
  const isCashflow = matchPath(pathname, Routes.cashflow);

  // When filtersOverride is provided, use it as the initial state until the server returns filtersList.
  // Once the store is populated by the server, the store's state takes over.
  const [overrideState, setOverrideState] = useState<ReportFilters>(() => filtersOverride || {});

  // 3. NOW CALLBACKS CAN SAFELY REFERENCE THE ACTIONS
  // Locked handleChange: prevents Investor filter from being modified while allowing other filters to change.
  const lockedHandleChange = useCallback(
    (filters: Record<string, (number | string | boolean)[]>) => {
      const investor = stateFilters["Investor"] || overrideState["Investor"] || [];
      const { Investor: _, ...rest } = filters;
      setQueriesAction({ filters: { ...rest, Investor: investor } });
      if (show_global_investor_filter) {
        updateQueries({ filters: { ...rest, Investor: investor } });
      }
    },
    [stateFilters, overrideState, setQueriesAction, updateQueries, show_global_investor_filter]
  );

  const { data: filtersList, action: fetchFilters } = useActionHook(getFilters);

  // Strip Investor from filtersList so it never appears in the "Add Filter" dropdown.
  const displayedFiltersList = hideInvestorFilter && filtersList
    ? Object.fromEntries(
        Object.entries(filtersList).filter(([key]) => key.toLowerCase() !== "investor")
      )
    : filtersList;

  const handleDateRangeChange = useCallback(
    (data: DateRange & { value: DateRangeValueType }, use_accounting_date?: boolean) => {
      const { to, from } = data;
      use_accounting_date = use_accounting_date || false;
      if ((to && isInTheFuture(to)) || (from && isInTheFuture(from))) {
        noticeModal({
          msg: "Notice - Report can not show future values of different currency. Different currency's in future dates are ignored."
        });
      }

      if (to && from && from > to) {
        // eslint-disable-next-line quotes
        return errorModal({ msg: '"Date From" must be less than "Date To"' });
      }

      setQueriesAction({
        dateRange: data,
        use_accounting_date
      });
    },
    [noticeModal, errorModal, setQueriesAction]
  );

  const getReportsFilters = useCallback(
    async (filters?: ReportFilters) => {
      await fetchFilters(filters || {});
    },
    [fetchFilters]
  );

  // Keep track of the last filters sent to avoid infinite loops
  const lastFiltersRef = React.useRef<ReportFilters>({});

  useEffect(() => {
    // getReportsFilters(stateFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!Object.keys(stateFilters).length) return;
    
    // Prevent infinite loop by checking if filters have actually changed
    const filtersChanged = JSON.stringify(stateFilters) !== JSON.stringify(lastFiltersRef.current);
    if (filtersChanged) {
      lastFiltersRef.current = stateFilters;
      getReportsFilters(stateFilters);
    }
  }, [stateFilters, getReportsFilters]);

  // Sync filtersOverride (e.g., scoped Investor names) into the store on mount or when changed.
  // This allows a parent (e.g., ReportingTab) to inject a pre-selected set of investors.
  useEffect(() => {
    if (!filtersOverride || Object.keys(filtersOverride).length === 0) {
      return;
    }
    // Merge override into current store filters rather than replacing entirely.
    setQueriesAction({ filters: { ...stateFilters, ...filtersOverride } });
    setOverrideState(filtersOverride);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersOverride, setQueriesAction]);

  // Once the server has returned data, merge it with any override values so the override
  // persists through the async fetch.
  useEffect(() => {
    if (!filtersOverride || Object.keys(filtersOverride).length === 0) {
      return;
    }
    if (Object.keys(stateFilters).length > 0 || filtersList) {
      setOverrideState((prev) => ({ ...prev, ...stateFilters }));
    }
  }, [filtersList, filtersOverride, stateFilters]);

  return (
    <Stack
      sx={{
        alignItems: "stretch",
        color: "#0a3243",
        position: "sticky",
        top: 0,
        gap: "16px",
        padding: 0,
        marginBottom: "16px",
        // margin: "0 -5px",
        zIndex: 1
      }}
    >
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        {showNavBar && (
          <Stack direction="row" gap={"15px"} alignItems="center">
            <HorizontalNavBar
              tabs={[
              {
                value: "overview",
                label: "Overview",
                icon: <GridViewIcon sx={iconsSx} />,
                to: Routes.snapshot,
                matchRouts: [Routes.snapshot]
              },
              {
                value: "classic",
                label: "Classic",
                icon: <AnalyticsOutlinedIcon sx={iconsSx} />,
                to: Routes.classic,
                matchRouts: [Routes.classic]
              },
              {
                value: "timeline",
                label: "Timeline",
                icon: <InsightsOutlinedIcon sx={iconsSx} />,
                to: Routes.timeline,
                matchRouts: [Routes.timeline]
              },
              ...(show_cashflow
                ? [
                    {
                      value: "cashflow",
                      label: "Forecast",
                      icon: <InsertChartOutlinedIcon sx={iconsSx} />,
                      to: Routes.cashflow,
                      matchRouts: [Routes.cashflow]
                    }
                  ]
                : [])
            ]}
          />
        </Stack>
        )}
      </Stack>
      {showFilters && (
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          sx={{
            width: "100%",
            bgcolor: "white",
            borderRadius: "8px",
            boxShadow: "0 1px 2px 0 #1212171a",
            alignItems: "stretch",
            p: "16px",
            maxHeight: "140px",
            overflow: "auto",
            "& > *": {
              flex: 1,
              maxWidth: "50%"
            }
          }}
        >
          <InlineFilter
            state={overrideState}
            filtersList={displayedFiltersList || {}}
            handleChangeFilter={lockInvestorSelection ? lockedHandleChange : (filters) => {
              setQueriesAction({ filters });
              show_global_investor_filter && updateQueries({ filters });
            }}
            applyMode={!disable_apply_filters}
            refetchFilters={(filters) =>
              disable_apply_filters ? getReportsFilters(stateFilters) : getReportsFilters(filters)
            }
            {...(isCashflow && { beginningElement: <SetPicker /> })}
          />
          <Stack direction="row" sx={{ gap: "10px", alignItems: "center", justifyContent: "flex-end" }}>
            <NumberFormat />
            {location.pathname !== Routes.dealReport && (
              <SelectDateRange
                formControlProps={{ sx: { minWidth: "200px" } }}
                selectProps={{
                  startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px" }} />
                }}
                label=""
                value={dateRange}
                onChange={handleDateRangeChange}
              />
            )}
            {showGroupByMenu && (
              <>
                <GroupByMenu>
                  {showColumns ? (
                    <SelectColumnContent
                      selectedColumns={comparisonColumns}
                      queriedDateRange={dateRange}
                      queriedSecondaryDateRange={secondaryDateRange}
                    />
                  ) : null}
                  <GroupByContent
                    showGroupBy={showGroupBy}
                    showRowBy={showRowBy}
                    showCharacteristics={showCharacteristics}
                  />
                </GroupByMenu>
              </>
            )}
            {showCurrency && (
              <>
                <SelectCurrency />
              </>
            )}
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <ContextMenuReports />
            </RoleComponentGuard>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};